import React, { MouseEventHandler, MutableRefObject, useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  Divider,
  MenuItem,
  Select as MuiSelect,
  Popover,
  PopoverProps,
  Stack,
  Switch,
  Typography
} from "@mui/material";
import { ManualQuarterRange } from "components/forms/Select-date-range/ManualQuarterRange";

import { currenciesArray } from "../../../constants";
import { useReportsQueriesSelector } from "../../../store";
import { CurrencyType, DateRangeValue, ReportsQueries, ReportTableColumn } from "../../../store/types";
import { Select, SelectDateRange } from "../../forms";
import { ManualDateRange } from "../../forms/Select-date-range/ManualDateRange";

export type ColumnSettingsPopoverProps = {
  isDuplicate: boolean;
  column?: ReportTableColumn;
  isBenchmarkTable?: boolean;
  hideSetDefault?: boolean;
  onApply: (
    queries?: Pick<ReportsQueries, "dateRange" | "currency" | "use_accounting_date"> &
      Pick<ReportTableColumn, "abbreviated" | "decimal" | "periodBeginning">
  ) => void;
};
type Props = ColumnSettingsPopoverProps;

const switchSx = {
  width: 48,
  height: 24,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: "2px",
    transitionDuration: "300ms",
    color: "#bac7ce",
    "&.Mui-checked": {
      transform: "translateX(23px)",
      color: "#0a3142",
      "& + .MuiSwitch-track": {
        backgroundColor: "#dae0e2",
        opacity: 1,
        border: 0
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5
      }
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff"
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      opacity: 0.7
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: 0.3
    }
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 20,
    height: 20
  },
  "& .MuiSwitch-track": {
    borderRadius: "34px",
    backgroundColor: "#dae0e2",
    opacity: 1,
    transition: "background-color 500ms"
  }
};

const PopoverContent = (
  props: Required<Props> & {
    isBenchmarkTable: boolean;
    hideSetDefault: boolean;
    onClose: MouseEventHandler;
    popoverRef: MutableRefObject<any>;
  }
) => {
  const {
    dateRange: defaultDateRange,
    currency: defaultCurrency,
    use_accounting_date: defaultUseAccountingDate
  } = useReportsQueriesSelector();
  const initialDateRange = props.column.dateRange || defaultDateRange;
  // Infer fromInception from the column's dateRange: if value is "dateRange" and from is undefined, fromInception was likely true
  const initialFromInception =
    initialDateRange.value === "dateRange" && initialDateRange.from === undefined ? true : false;
  const [fromInception, setFromInception] = useState<boolean>(initialFromInception);
  const [dateRange, setDateRange] = useState<DateRangeValue>(initialDateRange);
  const [currency, setCurrency] = useState<CurrencyType>(props.column.currency || defaultCurrency);
  const [abbreviatedState, setAbbreviatedState] = useState<boolean>(props.column.abbreviated || false);
  const [decimalState, setDecimalState] = useState<number>(props.column.decimal ?? 2);
  const [useAccountingDate, setUseAccountingDate] = useState<boolean>(!!defaultUseAccountingDate);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState<boolean>(false);
  const [periodBeginningState, setPeriodBeginningState] = useState<boolean>(props.column.periodBeginning || false);
  const [displayAs, setDisplayAs] = useState<string>("value");

  const setDateFrom = (from: string | null) => {
    setDateRange((dateRange) => ({ value: "dateRange", from: from || undefined, to: dateRange.to }));
  };

  const setDateTo = (to: string | null) => {
    setDateRange((dateRange) => ({ value: "dateRange", from: dateRange.from, to: to || undefined }));
  };

  const setDateFromQuarter = (from: string | null) => {
    setDateRange((dateRange) => ({ value: "selectQuarter", from: from || undefined, to: dateRange.to }));
  };

  const setDateToQuarter = (to: string | null) => {
    setDateRange((dateRange) => ({ value: "selectQuarter", from: dateRange.from, to: to || undefined }));
  };

  const handleApply = () => {
    const effectiveDateRange = fromInception ? { ...dateRange, from: undefined } : dateRange;
    props.onApply({
      dateRange: effectiveDateRange,
      currency,
      abbreviated: abbreviatedState,
      decimal: decimalState,
      use_accounting_date: useAccountingDate,
      periodBeginning: periodBeginningState
    });
  };

  const handleSetDefault = () => {
    props.onApply();
  };

  // Sync fromInception and dateRange states when column changes
  useEffect(() => {
    const columnDateRange = props.column.dateRange || defaultDateRange;
    setDateRange(columnDateRange);
    if (columnDateRange.value === "dateRange" && columnDateRange.from === undefined) {
      setFromInception(true);
    } else if (columnDateRange.value === "dateRange" && columnDateRange.from !== undefined) {
      setFromInception(false);
    }
  }, [props.column.dateRange, defaultDateRange]);

  useEffect(() => {
    if (dateRange.value === "dateRange") {
      setTimeout(() => {
        props.popoverRef.current?.updatePosition();
      }, 0);
    }
    if (dateRange.value === "selectQuarter") {
      setTimeout(() => {
        props.popoverRef.current?.updatePosition();
      }, 0);
    }
  }, [dateRange]);

  useEffect(() => {
    setTimeout(() => {
      props.popoverRef.current?.updatePosition();
    }, 0);
  }, [showAdvancedSettings]);

  return (
    <Stack sx={{ maxWidth: 450, padding: "20px", gap: "20px" }}>
      <Typography variant="h6" sx={{ mb: "10px" }}>
        {props.isDuplicate ? "New Column Settings" : "Column Settings"}
      </Typography>
      <SelectDateRange
        selectProps={{
          sx: {
            "&.MuiInputBase-root": { height: 40, borderRadius: "8px" }
          }
        }}
        dateRangeDialog={false}
        quarterRangeDialog={false}
        value={dateRange}
        onChange={setDateRange}
        label="Time Frame"
      />
      {!props.isBenchmarkTable && (
        <Select
          variant="outlined"
          label={"Currency"}
          value={currency}
          items={[{ key: "ORIGINAL", value: "ORIGINAL", text: "Original Currency" }, ...currenciesArray]}
          onChange={(e) => setCurrency(e.target.value as CurrencyType)}
          sx={{
            "&.MuiInputBase-root": { height: 40, borderRadius: "8px" }
          }}
        />
      )}
      {dateRange.value == "dateRange" && (
        <>
          <Divider sx={{ borderColor: "#DBE8F5" }} />
          <ManualDateRange
            from={dateRange.from || null}
            to={dateRange.to || null}
            setFrom={setDateFrom}
            setTo={setDateTo}
            fromInception={fromInception}
            setFromInception={setFromInception}
            useAccountingDate={useAccountingDate}
            setUseAccountingDate={setUseAccountingDate}
            fromLabel="From"
            toLabel="To"
          />
        </>
      )}
      {dateRange.value === "selectQuarter" && (
        <>
          <Divider sx={{ borderColor: "#DBE8F5" }} />
          <ManualQuarterRange
            from={dateRange.from || null}
            to={dateRange.to || null}
            setFrom={setDateFromQuarter}
            setTo={setDateToQuarter}
            fromInception={fromInception}
            setFromInception={setFromInception}
            useAccountingDate={useAccountingDate}
            setUseAccountingDate={setUseAccountingDate}
            fromLabel="Year From"
            toLabel="Year To"
          />
        </>
      )}
      {!props.isBenchmarkTable && (
        <Stack gap="10px">
          <Stack flexDirection="row" alignItems="center" gap="10px" justifyContent="space-between">
            <Stack>
              <Typography>Abbreviated Number Format</Typography>
              <Typography sx={{ color: "#92A7BC", fontSize: "14px" }}>{"(Billion / Million / Thousand)"}</Typography>
            </Stack>
            <Switch
              focusVisibleClassName=".Mui-focusVisible"
              checked={abbreviatedState}
              onChange={() =>
                setAbbreviatedState((prev) => {
                  return !prev;
                })
              }
              sx={switchSx}
            />
          </Stack>
          <Stack flexDirection="row" alignItems="center" gap="10px" justifyContent="space-between">
            <Typography>Decimal Places</Typography>
            <MuiSelect
              sx={{
                "&.MuiInputBase-root": { height: 40, borderRadius: "8px" },
                width: "140px",
                padding: "0px",
                ".MuiSelect-select": {
                  padding: "6px"
                }
              }}
              value={decimalState}
              onChange={(e) => setDecimalState(Number(e.target.value))}
            >
              {[0, 1, 2, 3, 4, 5, 6].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </MuiSelect>
          </Stack>
          <Divider sx={{ borderColor: "#DBE8F5" }} />
          <Typography
            onClick={() => setShowAdvancedSettings((prev) => !prev)}
            sx={{ color: "#26A6AE", cursor: "pointer", fontWeight: 500 }}
          >
            {showAdvancedSettings ? "Hide Advanced Settings" : "Show Advanced Settings"}
          </Typography>
          {showAdvancedSettings && (
            <>
              <Stack flexDirection="row" alignItems="center" gap="10px" justifyContent="space-between">
                <Stack>
                  <Typography>Start of period</Typography>
                  <Typography sx={{ color: "#92A7BC", fontSize: "14px" }}>
                    Values as of the start of the selected time frame. Moves when the page time frame changes.
                  </Typography>
                </Stack>
                <Switch
                  focusVisibleClassName=".Mui-focusVisible"
                  checked={periodBeginningState}
                  onChange={() => setPeriodBeginningState((prev) => !prev)}
                  sx={switchSx}
                />
              </Stack>
              {/* TODO: wire up "Display as" (value vs. % of total/parent row) once that calculation is implemented */}
              <Stack gap="4px" sx={{ display: "none" }}>
                <Stack flexDirection="row" alignItems="center" gap="10px" justifyContent="space-between">
                  <Typography>Display as</Typography>
                  <MuiSelect
                    sx={{
                      "&.MuiInputBase-root": { height: 40, borderRadius: "8px" },
                      width: "160px",
                      padding: "0px",
                      ".MuiSelect-select": {
                        padding: "6px"
                      }
                    }}
                    value={displayAs}
                    onChange={(e) => setDisplayAs(e.target.value)}
                  >
                    <MenuItem value="value">Value</MenuItem>
                    <MenuItem value="percentOfTotal">% of total</MenuItem>
                  </MuiSelect>
                </Stack>
                <Typography sx={{ color: "#92A7BC", fontSize: "14px" }}>
                  Show the value, or its share of the grand total or parent row
                </Typography>
              </Stack>
            </>
          )}
        </Stack>
      )}
      <div className="footer indent">
        <Stack flexDirection="row" justifyContent="flex-end" alignItems="center" gap="10px">
          {!props.isBenchmarkTable && !props.hideSetDefault && (
            <Button sx={{ color: "#26A6AE" }} variant="text" onClick={handleSetDefault}>
              Set Default
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="outlined" sx={{ minWidth: "53px", height: 32 }} onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            sx={{ backgroundColor: "#071F35", minWidth: "46px", height: 32 }}
          >
            Apply
          </Button>
        </Stack>
      </div>
    </Stack>
  );
};

export const ColumnSettingsPopover = ({
  isDuplicate,
  column,
  onApply,
  isBenchmarkTable,
  hideSetDefault,
  ...popoverProps
}: PopoverProps & Props) => {
  const popoverRef = useRef<any>();

  return (
    <Popover
      sx={{ ".MuiPopover-paper": { overflow: "visible !important" } }}
      action={popoverRef}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right"
      }}
      {...popoverProps}
    >
      {column && (
        <PopoverContent
          isBenchmarkTable={!!isBenchmarkTable}
          hideSetDefault={!!hideSetDefault}
          popoverRef={popoverRef}
          isDuplicate={isDuplicate}
          column={column}
          onApply={onApply}
          onClose={(e) => popoverProps.onClose?.(e, "backdropClick")}
        />
      )}
    </Popover>
  );
};
